import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  FOOD_CATEGORIES,
  FOOD_DETAILS,
  FOOD_FAMILIES,
  FOOD_STATES,
  FOOD_TAXONOMY_VERSION,
  HORIZON_RULES,
  suggestRotationRuleId,
} from '../content/catalog';
import type {
  CreateContainerInput,
  FoodDateCertainty,
  FoodContainer,
  InventorySnapshot,
  ManufacturerDateType,
  QuantityUnit,
} from '../domain/model';
import {
  isFridge,
  isPantry,
  sharesStorageFamily,
  storagePolicyFor,
} from '../domain/storage-policy';
import { useI18n } from '../i18n/I18nContext';
import type { MessageKey } from '../i18n/catalog';
import { taxonomyLabel } from '../i18n/domain-labels';
import { useApp } from './AppContext';
import {
  loadHouseholdFavorites,
  saveHouseholdFavorites,
} from '../infrastructure/household-artifacts';
import {
  Button,
  categoryLabel,
  containerDisplayName,
  containerReference,
  Field,
  Icon,
  Modal,
  SelectField,
  UnitOptions,
  formatDate,
  formatQuantity,
  locationLabel,
  statusLabel,
  snapshotDisplayLabel,
  unitLabel,
} from './components';

const localToday = (): string => {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const numberOrNull = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

interface SpeechRecognitionResultLike {
  readonly transcript: string;
}

interface SpeechRecognitionEventLike {
  readonly results: ArrayLike<ArrayLike<SpeechRecognitionResultLike>>;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

interface DetectedBarcodeLike {
  readonly rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: ImageBitmap): Promise<DetectedBarcodeLike[]>;
}

type BarcodeDetectorConstructor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

const speechRecognitionConstructor = (): SpeechRecognitionConstructor | null => {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
};

const barcodeDetectorConstructor = (): BarcodeDetectorConstructor | null => {
  if (typeof window === 'undefined' || typeof createImageBitmap !== 'function') return null;
  return (window as typeof window & { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector ?? null;
};

export const AddContainerModal = ({
  snapshot,
  preferredFamily,
  onClose,
}: {
  snapshot: InventorySnapshot;
  preferredFamily?: 'fridge' | 'freezer' | null;
  onClose: () => void;
}) => {
  const i18n = useI18n();
  const { locale, t, tp } = i18n;
  const { service, performMutation, busyKeys } = useApp();
  const activeAppliances = snapshot.freezers.filter((appliance) => appliance.archivedAt == null);
  const preferredAppliances = activeAppliances.filter(
    (appliance) =>
      preferredFamily == null
        ? true
        : preferredFamily === 'fridge'
          ? appliance.equipmentKind === 'fridge'
          : storagePolicyFor(appliance.equipmentKind).family === 'freezer',
  );
  const activeApplianceIds = new Set(activeAppliances.map((appliance) => appliance.id));
  const activeLocations = snapshot.locations.filter(
    (location) => location.archivedAt == null && activeApplianceIds.has(location.freezerId),
  );
  const preferredApplianceIds = new Set(preferredAppliances.map((appliance) => appliance.id));
  const preferredLocation = preferredFamily == null
    ? undefined
    : activeLocations.find((location) => preferredApplianceIds.has(location.freezerId))?.id;
  const fallbackLocation = activeLocations.some(
    (location) => location.id === snapshot.preferences?.lastLocationId,
  )
    ? snapshot.preferences?.lastLocationId ?? ''
    : activeLocations[0]?.id ?? '';
  const defaultLocation = preferredFamily == null ? fallbackLocation : preferredLocation ?? '';
  const defaultFreezer = preferredFamily == null
    ? snapshot.locations.find((location) => location.id === defaultLocation)?.freezerId ??
      activeAppliances[0]?.id ??
      ''
    : snapshot.locations.find((location) => location.id === defaultLocation)?.freezerId ??
      preferredAppliances[0]?.id ??
      '';
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState(defaultLocation);
  const [freezerId, setFreezerId] = useState(defaultFreezer);
  const [categoryId, setCategoryId] = useState('');
  const [family, setFamily] = useState('');
  const [detail, setDetail] = useState('');
  const [foodState, setFoodState] = useState<Parameters<typeof suggestRotationRuleId>[1]>('');
  const [search, setSearch] = useState('');
  const [barcode, setBarcode] = useState('');
  const [barcodeState, setBarcodeState] = useState<'none' | 'known' | 'new' | 'error'>('none');
  const [barcodeScanBusy, setBarcodeScanBusy] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceError, setVoiceError] = useState(false);
  const voiceRecognition = useRef<SpeechRecognitionLike | null>(null);
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState<QuantityUnit | ''>('portion');
  const [copyCount, setCopyCount] = useState(1);
  const [completedCopies, setCompletedCopies] = useState(0);
  const [writtenConfirmed, setWrittenConfirmed] = useState(false);
  const [rotationConfirmed, setRotationConfirmed] = useState(false);
  const [favoriteNames, setFavoriteNames] = useState<string[]>(() => {
    return snapshot.household == null ? [] : loadHouseholdFavorites(snapshot.household.id);
  });
  const [storageStartedOn, setStorageStartedOn] = useState(localToday());
  const [storageDateCertainty, setStorageDateCertainty] = useState<FoodDateCertainty>('known');
  const [purchasedOn, setPurchasedOn] = useState('');
  const [purchaseDateCertainty, setPurchaseDateCertainty] = useState<FoodDateCertainty>('known');
  const [openedOn, setOpenedOn] = useState('');
  const [openDateCertainty, setOpenDateCertainty] = useState<FoodDateCertainty>('known');
  const [useFirstOn, setUseFirstOn] = useState('');
  const [useFirstDateCertainty, setUseFirstDateCertainty] = useState<FoodDateCertainty>('known');
  const [manufacturerType, setManufacturerType] = useState<ManufacturerDateType>('none');
  const [manufacturerDate, setManufacturerDate] = useState('');
  const [manufacturerDateCertainty, setManufacturerDateCertainty] = useState<FoodDateCertainty>('known');
  const [horizonRuleId, setHorizonRuleId] = useState('rotation-none');
  const [manualHorizon, setManualHorizon] = useState('');
  const [notes, setNotes] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [draft, setDraft] = useState<FoodContainer | null>(null);
  const recent = [...snapshot.containers]
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .filter((container, index, values) =>
      values.findIndex((candidate) => candidate.name === container.name) === index,
    )
    .slice(0, 3);
  const selectedName =
    name.trim() ||
    [detail ? taxonomyLabel(detail, locale) : family ? taxonomyLabel(family, locale) : categoryId ? categoryLabel(categoryId, locale) : '', foodState ? t(`entry.state.${foodState === 'Cru' ? 'raw' : foodState === 'Cuit' ? 'cooked' : 'prepared'}` as MessageKey) : ''].filter(Boolean).join(' — ');
  const families = FOOD_FAMILIES[categoryId] ?? [];
  const details = FOOD_DETAILS[family] ?? [];
  const locationsForFreezer = snapshot.locations.filter(
    (location) => location.freezerId === freezerId && location.archivedAt == null,
  );
  const resolvedLocationId =
    locationId && locationsForFreezer.some((location) => location.id === locationId)
      ? locationId
      : locationsForFreezer[0]?.id ?? '';
  const selectedAppliance = snapshot.freezers.find((freezer) => freezer.id === freezerId);
  const selectedPolicy = storagePolicyFor(selectedAppliance?.equipmentKind ?? 'unknown');
  const fridgeEntry = isFridge(selectedAppliance);
  const pantryEntry = isPantry(selectedAppliance);
  const internalEntry = selectedPolicy.trackingMode === 'internal-only';
  const barcodeValid = barcode === '' || /^\d{8,14}$/u.test(barcode);
  const barcodeScanAvailable = barcodeDetectorConstructor() != null;
  const adoptBarcode = (rawValue: string) => {
    const normalized = rawValue.replace(/\D/gu, '').slice(0, 14);
    setBarcode(normalized);
    if (!/^\d{8,14}$/u.test(normalized)) {
      setBarcodeState('none');
      return;
    }
    const known = [...snapshot.containers]
      .filter((container) => container.barcode === normalized)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0];
    if (known == null) {
      setBarcodeState('new');
      return;
    }
    setName(known.name);
    setSearch(known.name);
    setCategoryId(known.categoryId);
    setQuantity(known.initialQuantity?.toString() ?? '');
    setUnit(known.unit ?? 'portion');
    setBarcodeState('known');
  };
  const scanBarcodeImage = async (file: File | null) => {
    const Detector = barcodeDetectorConstructor();
    if (file == null || Detector == null || barcodeScanBusy) return;
    setBarcodeScanBusy(true);
    setBarcodeState('none');
    let image: ImageBitmap | null = null;
    try {
      image = await createImageBitmap(file);
      const detector = new Detector({ formats: ['ean_8', 'ean_13', 'upc_a', 'upc_e'] });
      const result = (await detector.detect(image)).find((candidate) => /^\d{8,14}$/u.test(candidate.rawValue));
      if (result == null) {
        setBarcodeState('error');
      } else {
        adoptBarcode(result.rawValue);
      }
    } catch {
      setBarcodeState('error');
    } finally {
      image?.close();
      setBarcodeScanBusy(false);
    }
  };
  const voiceAvailable =
    import.meta.env.VITE_OPTIONAL_VOICE_ENTRY === '1' &&
    speechRecognitionConstructor() != null;
  useEffect(() => () => {
    const recognition = voiceRecognition.current;
    if (recognition == null) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition.abort();
    voiceRecognition.current = null;
  }, []);
  const startVoiceEntry = () => {
    const Recognition = speechRecognitionConstructor();
    if (Recognition == null || voiceListening) return;
    const recognition = new Recognition();
    voiceRecognition.current = recognition;
    recognition.lang = locale.startsWith('fr') ? 'fr-FR' : 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript.trim() ?? '';
      if (transcript) {
        setSearch(transcript);
        setName(transcript);
        setVoiceError(false);
      }
    };
    recognition.onerror = () => {
      setVoiceError(true);
      setVoiceListening(false);
    };
    recognition.onend = () => {
      setVoiceListening(false);
      voiceRecognition.current = null;
    };
    setVoiceError(false);
    setVoiceListening(true);
    try {
      recognition.start();
    } catch {
      recognition.abort();
      voiceRecognition.current = null;
      setVoiceListening(false);
      setVoiceError(true);
    }
  };
  const toggleFavorite = () => {
    if (!selectedName) return;
    const next = favoriteNames.includes(selectedName)
      ? favoriteNames.filter((item) => item !== selectedName)
      : [selectedName, ...favoriteNames].slice(0, 12);
    setFavoriteNames(next);
    if (snapshot.household != null) {
      saveHouseholdFavorites(snapshot.household.id, next);
    }
  };

  const buildInput = (): CreateContainerInput => ({
    name: selectedName,
    barcode: barcode || null,
    categoryId: categoryId || 'other',
    locationId: resolvedLocationId,
    quantity: numberOrNull(quantity),
    unit: quantity.trim() === '' ? null : (unit || null),
    storageStartedOn,
    dateEvidence: {
      storageStartedOn: { source: 'household', certainty: storageDateCertainty },
      purchasedOn: internalEntry && purchasedOn ? { source: 'household', certainty: purchaseDateCertainty } : null,
      openedOn: internalEntry && openedOn ? { source: 'household', certainty: openDateCertainty } : null,
      useFirstOn: internalEntry && useFirstOn ? { source: 'household', certainty: useFirstDateCertainty } : null,
      manufacturerDate: manufacturerType !== 'none' && manufacturerDate ? { source: 'packaging', certainty: manufacturerDateCertainty } : null,
    },
    purchasedOn: internalEntry ? purchasedOn || null : null,
    openedOn: internalEntry ? openedOn || null : null,
    useFirstOn: internalEntry ? useFirstOn || null : null,
    manufacturerDateType: manufacturerType,
    manufacturerDate: manufacturerType === 'none' || !manufacturerDate ? null : manufacturerDate,
    horizonRule:
      selectedPolicy.allowsFreezerHorizon
        ? HORIZON_RULES.find((rule) => rule.id === horizonRuleId) ?? null
        : null,
    qualityHorizonOn: selectedPolicy.allowsFreezerHorizon ? manualHorizon || null : null,
    notes: [notes, `Taxonomie ${FOOD_TAXONOMY_VERSION}`].filter(Boolean).join('\n'),
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const mutation = await performMutation(
      'create-container',
      () => service.createContainer(buildInput()),
      fridgeEntry ? t('entry.fridgeAdded') : pantryEntry ? t('entry.pantryAdded') : undefined,
    );
    if (mutation != null) {
      if (mutation.container.trackingMode === 'internal-only') {
        onClose();
      } else {
        setDraft(mutation.container);
      }
    }
  };

  const confirmAndClose = async () => {
    if (
      draft == null ||
      !writtenConfirmed ||
      (draft.qualityHorizonOn != null && !rotationConfirmed) ||
      !resolvedLocationId
    ) return;
    const mutation = await performMutation(
      `finalize:${draft.id}`,
      () => service.finalizeReservation(draft, resolvedLocationId),
      t('entry.numberReady', { number: draft.serialNumber ?? '' }),
    );
    if (mutation == null) return;
    const nextCompleted = completedCopies + 1;
    if (nextCompleted >= copyCount) {
      onClose();
      return;
    }
    const duplicate = await performMutation(
      `duplicate:${mutation.container.id}:${nextCompleted}`,
      () => service.duplicate(mutation.container),
      t('entry.copyConfirmed', { number: nextCompleted }),
    );
    if (duplicate != null) {
      setCompletedCopies(nextCompleted);
      setDraft(duplicate.container);
      setWrittenConfirmed(false);
      setRotationConfirmed(false);
    }
  };

  const abandonAndClose = async () => {
    if (draft == null) {
      onClose();
      return;
    }
    const mutation = await performMutation(
      `abandon:${draft.id}`,
      () => service.abandonReservation(draft),
    );
    if (mutation != null) onClose();
  };

  if (draft != null) {
    const latestDraft = snapshot.containers.find((item) => item.id === draft.id);
    const reservationChanged = latestDraft == null || latestDraft.version !== draft.version;
    return (
      <Modal
        title={t('entry.numberAssigned')}
        description={t('entry.portionProgress', { current: completedCopies + 1, total: copyCount })}
        onClose={() => reservationChanged ? onClose() : void abandonAndClose()}
      >
        {reservationChanged ? (
          <div className="notice notice--neutral">
            <p>{t('error.staleContainer')}</p>
            <Button onClick={() => {
              if (latestDraft?.status !== 'draft') { onClose(); return; }
              setDraft(latestDraft);
              setWrittenConfirmed(false);
              setRotationConfirmed(false);
            }}>{latestDraft?.status === 'draft' ? t('entry.reviewReservation') : t('common.back')}</Button>
          </div>
        ) : null}
        <div className="write-number write-number--sticky">
          <span>{t('entry.writeOnContainer')}</span>
          <strong aria-label={t('container.numberAria', { number: draft.serialNumber ?? '' })}>{draft.serialNumber}</strong>
          <p>{draft.name}</p>
        </div>
        <div className="notice notice--info">
          <Icon name="info" />
          <p>
            {draft.numberAssignmentKind === 'reused'
              ? t('entry.numberReusedHelp')
              : t('entry.numberReservedHelp')}
          </p>
        </div>
        <div className="form-stack">
          <SelectField label={t('equipment.freezer')} value={freezerId} onChange={(event) => {
            const nextFreezer = event.target.value;
            setFreezerId(nextFreezer);
            setLocationId(snapshot.locations.find(
              (location) => location.freezerId === nextFreezer && location.archivedAt == null,
            )?.id ?? '');
          }} required>
            {snapshot.freezers.filter((freezer) => freezer.archivedAt == null && storagePolicyFor(freezer.equipmentKind).trackingMode === 'visible-number').map((freezer) => (
              <option key={freezer.id} value={freezer.id}>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</option>
            ))}
          </SelectField>
          <SelectField label={t('location.optional')} value={locationId} onChange={(event) => setLocationId(event.target.value)}>
            <option value="">{t('location.none')}</option>
            {locationsForFreezer.map((location) => (
              <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>
            ))}
          </SelectField>
          <div className="entry-summary">
            <strong>{t('entry.summary')}</strong>
            <span>{draft.name}{t('common.separator')}{formatQuantity(draft, locale)}</span>
            <span>{snapshotDisplayLabel(snapshot, snapshot.freezers.find((freezer) => freezer.id === freezerId)?.name ?? '', locale)}</span>
            <span>{locationId ? locationLabel(snapshot, locationId, locale) : t('location.unspecified')}</span>
            {draft.qualityHorizonOn != null ? (
              <>
                <span>{t('entry.bestUseDate', { date: formatDate(draft.qualityHorizonOn, locale) })}</span>
                <small>
                  {draft.horizonRule == null ? t('entry.userDefinedDate') : t('rotation.source')}{t('common.separator')}
                  {t('entry.version', { version: draft.horizonRule?.version ?? t('entry.manualVersion') })}
                </small>
              </>
            ) : null}
          </div>
          {draft.qualityHorizonOn != null ? (
            <label className="check-row">
              <input type="checkbox" checked={rotationConfirmed} onChange={(event) => setRotationConfirmed(event.target.checked)} />
              <span>{t('entry.rotationConfirmed')}</span>
            </label>
          ) : null}
          <label className="check-row">
            <input type="checkbox" checked={writtenConfirmed} onChange={(event) => setWrittenConfirmed(event.target.checked)} />
            <span>{t('entry.numberWritten', { number: draft.serialNumber ?? '' })}</span>
          </label>
        </div>
        <div className="button-stack">
          <Button
            onClick={() => void confirmAndClose()}
            disabled={
              reservationChanged ||
              !writtenConfirmed ||
              (draft.qualityHorizonOn != null && !rotationConfirmed) ||
              !resolvedLocationId ||
              busyKeys.has(`finalize:${draft.id}`)
            }
            icon={<Icon name="check" />}
          >
            {completedCopies + 1 < copyCount ? t('entry.validateNext') : t('entry.validate')}
          </Button>
          <Button variant="ghost" onClick={() => void abandonAndClose()} disabled={reservationChanged}>{t('entry.abandon')}</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={t('entry.addTitle')}
      description={t('entry.addDescription')}
      onClose={onClose}
      wide
    >
      <form onSubmit={(event) => void submit(event)} className="form-stack">
        <Field
          label={t('entry.barcode')}
          value={barcode}
          inputMode="numeric"
          pattern="[0-9]{8,14}"
          onChange={(event) => {
            setBarcode(event.target.value.replace(/\D/gu, '').slice(0, 14));
            setBarcodeState('none');
          }}
          onBlur={() => adoptBarcode(barcode)}
          hint={barcodeValid ? t('entry.barcodeHelp') : t('entry.barcodeInvalid')}
          {...(barcodeValid ? {} : { error: t('entry.barcodeInvalid') })}
          maxLength={14}
        />
        {barcodeScanAvailable ? (
          <div className="barcode-scan-row">
            <label className={`button button--secondary ${barcodeScanBusy ? 'is-disabled' : ''}`}>
              <span>{barcodeScanBusy ? t('entry.barcodeScanning') : t('entry.barcodeScan')}</span>
              <input
                className="visually-hidden"
                type="file"
                accept="image/*"
                capture="environment"
                disabled={barcodeScanBusy}
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  event.target.value = '';
                  void scanBarcodeImage(file);
                }}
              />
            </label>
            <small aria-live="polite">
              {barcodeState === 'known'
                ? t('entry.barcodeKnown')
                : barcodeState === 'new'
                  ? t('entry.barcodeNew')
                  : barcodeState === 'error'
                    ? t('entry.barcodeNotRead')
                    : t('entry.barcodePrivacy')}
            </small>
          </div>
        ) : null}
        <Field
          autoFocus
          label={t('entry.search')}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setName(event.target.value);
          }}
          placeholder={t('entry.searchExample')}
          maxLength={160}
        />
        {voiceAvailable ? (
          <div className="entry-voice">
            <Button
              type="button"
              variant="secondary"
              disabled={voiceListening}
              onClick={startVoiceEntry}
            >
              {voiceListening ? t('entry.voiceListening') : t('entry.voiceStart')}
            </Button>
            <small aria-live="polite">
              {voiceError ? t('entry.voiceUnavailable') : t('entry.voiceHelp')}
            </small>
          </div>
        ) : null}
        {favoriteNames.length > 0 ? (
          <div className="entry-recents" aria-label={t('entry.localFavorites')}>
            <strong>{t('entry.favorites')}</strong>
            <div className="chip-row">
              {favoriteNames.map((favorite) => (
                <button type="button" key={favorite} onClick={() => {
                  setName(favorite);
                  setSearch(favorite);
                }}>★ {favorite}</button>
              ))}
            </div>
          </div>
        ) : null}
        {recent.length > 0 ? (
          <div className="entry-recents" aria-label={t('entry.recentChoices')}>
            <strong>{t('entry.recent')}</strong>
            <div className="chip-row">
              {recent.map((container) => (
                <button type="button" key={container.id} onClick={() => {
                  setName(container.name);
                  setSearch(container.name);
                  setBarcode(container.barcode ?? '');
                  setBarcodeState(container.barcode == null ? 'none' : 'known');
                  setCategoryId(container.categoryId);
                  setQuantity(container.initialQuantity?.toString() ?? '');
                  setUnit(container.unit ?? 'portion');
                }}>{containerDisplayName(snapshot, container, locale)}</button>
              ))}
            </div>
          </div>
        ) : null}
        {selectedName ? (
          <button className="text-link entry-favorite-toggle" type="button" onClick={toggleFavorite}>
            {favoriteNames.includes(selectedName) ? t('entry.removeFavorite') : t('entry.addFavorite')}
          </button>
        ) : null}
        <fieldset className="taxonomy-picker">
          <legend>{t('entry.category')}</legend>
          <div className="taxonomy-grid">
            {FOOD_CATEGORIES.map((category) => (
              <button
                type="button"
                key={category.id}
                className={categoryId === category.id ? 'is-selected' : ''}
                aria-pressed={categoryId === category.id}
                onClick={() => {
                  setCategoryId(category.id);
                  setFamily('');
                  setDetail('');
                  setHorizonRuleId(suggestRotationRuleId(category.id, foodState));
                }}
              >
                <span aria-hidden="true">{category.icon}</span>
                <span>{categoryLabel(category.id, locale)}</span>
              </button>
            ))}
          </div>
        </fieldset>
        {categoryId ? (
          <div className="progressive-taxonomy">
            <SelectField label={t('entry.family')} value={family} onChange={(event) => {
              setFamily(event.target.value);
              setDetail('');
            }}>
              <option value="">{t('entry.later')}</option>
              {families.map((item) => <option key={item} value={item}>{taxonomyLabel(item, locale)}</option>)}
            </SelectField>
            <Field label={t('entry.customFamily')} value={family} onChange={(event) => setFamily(event.target.value)} placeholder={t('entry.freeInput')} />
            {family ? (
              <>
                <SelectField label={t('entry.detail')} value={detail} onChange={(event) => setDetail(event.target.value)}>
                  <option value="">{t('entry.later')}</option>
                  {details.map((item) => <option key={item} value={item}>{taxonomyLabel(item, locale)}</option>)}
                </SelectField>
                <Field label={t('entry.customDetail')} value={detail} onChange={(event) => setDetail(event.target.value)} placeholder={t('entry.freeInput')} />
                <div className="segmented" role="group" aria-label={t('entry.foodState')}>
                  {FOOD_STATES.map((state) => (
                    <button type="button" key={state} className={foodState === state ? 'is-active' : ''} onClick={() => {
                      setFoodState(state);
                      setHorizonRuleId(suggestRotationRuleId(categoryId, state));
                    }}>{t(`entry.state.${state === 'Cru' ? 'raw' : state === 'Cuit' ? 'cooked' : 'prepared'}` as MessageKey)}</button>
                  ))}
                  <button type="button" className={foodState === '' ? 'is-active' : ''} onClick={() => setFoodState('')}>{t('entry.state.later')}</button>
                </div>
              </>
            ) : null}
          </div>
        ) : null}
        <div className="form-row">
          <SelectField label={t('equipment.appliance')} value={freezerId} onChange={(event) => {
            const nextFreezer = event.target.value;
            const nextAppliance = snapshot.freezers.find((freezer) => freezer.id === nextFreezer);
            setFreezerId(nextFreezer);
            setLocationId(snapshot.locations.find(
              (location) => location.freezerId === nextFreezer && location.archivedAt == null,
            )?.id ?? '');
            if (nextAppliance != null && storagePolicyFor(nextAppliance.equipmentKind).trackingMode === 'internal-only') {
              setCopyCount(1);
              setHorizonRuleId('rotation-none');
              setManualHorizon('');
            } else {
              setPurchasedOn('');
              setOpenedOn('');
              setUseFirstOn('');
            }
          }} required>
            {snapshot.freezers.filter((freezer) => freezer.archivedAt == null).map((freezer) => (
              <option key={freezer.id} value={freezer.id}>
                {snapshotDisplayLabel(snapshot, freezer.name, locale)}{t('common.separator')}{freezer.equipmentKind === 'fridge' ? t('equipment.fridge') : freezer.equipmentKind === 'pantry' ? t('equipment.pantry') : t('equipment.freezerLower')}
              </option>
            ))}
          </SelectField>
          <SelectField label={t('location.location')} value={locationId} onChange={(event) => setLocationId(event.target.value)} required>
            {locationsForFreezer.map((location) => (
              <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>
            ))}
          </SelectField>
        </div>
        <div className="form-row">
          <Field
            label={t('entry.quantityPerContainer')}
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            placeholder={t('entry.quantityExample')}
          />
          <SelectField
            label={t('unit.label')}
            value={quantity ? unit : ''}
            disabled={!quantity}
            onChange={(event) => setUnit(event.target.value as QuantityUnit | '')}
          >
            <UnitOptions />
          </SelectField>
        </div>
        <div className="chip-row" aria-label={t('entry.quickQuantities')}>
          {[1, 2, 4, 6].map((value) => (
            <button type="button" key={value} onClick={() => {
              setQuantity(String(value));
              setUnit('portion');
            }}>{tp('entry.portions', value)}</button>
          ))}
        </div>
        {!internalEntry ? (
          <label className="field">
            <span className="field__label">{t('entry.identicalContainers')}</span>
            <select value={copyCount} onChange={(event) => setCopyCount(Number(event.target.value))}>
              {[1, 2, 3, 4, 5, 6].map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <span className="field__hint">{t('entry.distinctNumberHelp')}</span>
          </label>
        ) : null}
        <div className="form-row">
          <Field
            label={fridgeEntry ? t('entry.storageFridgeDate') : pantryEntry ? t('entry.storagePantryDate') : t('entry.storageFreezerDate')}
            type="date"
            value={storageStartedOn}
            max={localToday()}
            onChange={(event) => setStorageStartedOn(event.target.value)}
            hint={t('dates.sourceHousehold')}
            required
          />
          <SelectField label={t('dates.certainty')} value={storageDateCertainty} onChange={(event) => setStorageDateCertainty(event.target.value as FoodDateCertainty)}>
            <option value="known">{t('dates.known')}</option>
            <option value="estimated">{t('dates.estimated')}</option>
          </SelectField>
        </div>

        {internalEntry ? (
          <section className="fridge-dates-panel" aria-labelledby="fridge-dates-title">
            <div>
              <strong id="fridge-dates-title">{pantryEntry ? t('entry.pantryReferences') : t('entry.fridgeReferences')}</strong>
              <span>{t('entry.noSafetyVerdict')}</span>
            </div>
            <div className="form-row">
              <Field
                label={t('entry.purchaseDateOptional')}
                type="date"
                value={purchasedOn}
                max={localToday()}
                onChange={(event) => setPurchasedOn(event.target.value)}
                hint={t('dates.sourceHousehold')}
              />
              <Field
                label={t('entry.openDateOptional')}
                type="date"
                value={openedOn}
                min={purchasedOn || undefined}
                max={localToday()}
                onChange={(event) => setOpenedOn(event.target.value)}
                hint={t('dates.sourceHousehold')}
              />
            </div>
            <div className="form-row">
              <SelectField label={t('dates.purchaseCertainty')} value={purchaseDateCertainty} onChange={(event) => setPurchaseDateCertainty(event.target.value as FoodDateCertainty)} disabled={!purchasedOn}>
                <option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option>
              </SelectField>
              <SelectField label={t('dates.openCertainty')} value={openDateCertainty} onChange={(event) => setOpenDateCertainty(event.target.value as FoodDateCertainty)} disabled={!openedOn}>
                <option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option>
              </SelectField>
            </div>
            <Field
              label={t('entry.useFirstOptional')}
              type="date"
              value={useFirstOn}
              onChange={(event) => setUseFirstOn(event.target.value)}
              hint={`${t('entry.useFirstHint')} ${t('dates.sourceHousehold')}`}
            />
            <SelectField label={t('dates.referenceCertainty')} value={useFirstDateCertainty} onChange={(event) => setUseFirstDateCertainty(event.target.value as FoodDateCertainty)} disabled={!useFirstOn}>
              <option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option>
            </SelectField>
          </section>
        ) : null}

        <button className="disclosure" type="button" onClick={() => setAdvanced((value) => !value)} aria-expanded={advanced}>
          <span>{fridgeEntry ? t('entry.advancedFridge') : pantryEntry ? t('entry.advancedPantry') : t('entry.advancedFreezer')}</span>
          <span aria-hidden="true">{advanced ? '−' : '+'}</span>
        </button>

        {advanced ? (
          <div className="advanced-fields">
            {!internalEntry ? (
              <>
                <SelectField
                  label={t('entry.referenceDuration')}
                  value={horizonRuleId}
                  onChange={(event) => setHorizonRuleId(event.target.value)}
                  hint={t('entry.referenceDurationHint')}
                >
                  {HORIZON_RULES.map((rule) => (
                    <option key={rule.id} value={rule.id}>{t(`rotation.${rule.id}` as MessageKey)}</option>
                  ))}
                </SelectField>
                <Field
                  label={t('entry.bestUseOptional')}
                  type="date"
                  value={manualHorizon}
                  onChange={(event) => setManualHorizon(event.target.value)}
                  hint={t('entry.bestUseOverride')}
                />
              </>
            ) : (
              <div className="notice notice--warning">
                <Icon name="warning" />
                <p>{pantryEntry ? t('entry.pantryHorizonWarning') : t('entry.fridgeHorizonWarning')}</p>
              </div>
            )}
            <div className="form-row">
              <SelectField
                label={t('entry.manufacturerDate')}
                value={manufacturerType}
                onChange={(event) => setManufacturerType(event.target.value as ManufacturerDateType)}
              >
                <option value="none">{t('entry.noManufacturerDate')}</option>
                <option value="DLC">{t('entry.dlc')}</option>
                <option value="DDM">{t('entry.ddm')}</option>
              </SelectField>
              <Field
                label={manufacturerType === 'none' ? t('entry.date') : t('entry.dateType', { type: manufacturerType })}
                type="date"
                value={manufacturerDate}
                disabled={manufacturerType === 'none'}
                required={manufacturerType !== 'none'}
                onChange={(event) => setManufacturerDate(event.target.value)}
                hint={t('dates.sourcePackaging')}
              />
            </div>
            <SelectField label={t('dates.manufacturerCertainty')} value={manufacturerDateCertainty} onChange={(event) => setManufacturerDateCertainty(event.target.value as FoodDateCertainty)} disabled={manufacturerType === 'none' || !manufacturerDate}>
              <option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option>
            </SelectField>
            <label className="field">
              <span className="field__label">{t('entry.optionalNotes')}</span>
              <textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={2_000} rows={3} />
            </label>
          </div>
        ) : null}

        <div className="notice notice--neutral">
          <Icon name={pantryEntry ? 'box' : 'snow'} />
          <p>
            {internalEntry
              ? pantryEntry ? t('entry.pantryNoNumber') : t('entry.fridgeNoNumber')
              : t('entry.freezerNoExtras')}
          </p>
        </div>
        <Button
          type="submit"
          disabled={
            !selectedName ||
            !resolvedLocationId ||
            !barcodeValid ||
            (quantity.trim() !== '' && !unit) ||
            busyKeys.has('create-container')
          }
          icon={<Icon name="plus" />}
        >
          {fridgeEntry ? t('entry.addFridge') : pantryEntry ? t('entry.addPantry') : t('entry.reserveNumber')}
        </Button>
      </form>
    </Modal>
  );
};

export const PartialModal = ({
  container,
  snapshot,
  onClose,
}: {
  container: FoodContainer;
  snapshot: InventorySnapshot;
  onClose: () => void;
}) => {
  const { formatNumber, locale, t } = useI18n();
  const { service, performMutation, busyKeys } = useApp();
  const [mode, setMode] = useState<'remaining' | 'used'>('remaining');
  const [value, setValue] = useState('');
  const [locationId, setLocationId] = useState(container.locationId);
  const current = container.remainingQuantity ?? 0;
  const sourceApplianceId =
    snapshot.locations.find((location) => location.id === container.locationId)?.freezerId ?? '';
  const sourceAppliance = snapshot.freezers.find((appliance) => appliance.id === sourceApplianceId);
  const compatibleApplianceIds = new Set(
    snapshot.freezers
      .filter(
        (appliance) =>
          sourceAppliance != null &&
          appliance.archivedAt == null &&
          sharesStorageFamily(sourceAppliance.equipmentKind, appliance.equipmentKind),
      )
      .map((appliance) => appliance.id),
  );
  const numeric = numberOrNull(value);
  const after = numeric == null ? null : mode === 'remaining' ? numeric : current - numeric;
  const key = `partial-modal:${container.id}`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (numeric == null || container.unit == null) return;
    const result = await performMutation(
      key,
      () => service.consumePart(container, {
        mode,
        value: numeric,
        unit: container.unit as QuantityUnit,
        replacementLocationId: locationId,
      }),
    );
    if (result != null) onClose();
  };

  return (
    <Modal
      title={
        container.serialNumber == null
          ? t('partial.titleItem', { reference: containerReference(container, locale) })
          : t('partial.titleNumber', { number: container.serialNumber ?? '' })
      }
      description={containerDisplayName(snapshot, container, locale)}
      onClose={onClose}
    >
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <div className="segmented" role="group" aria-label={t('partial.inputMode')}>
          <button type="button" className={mode === 'remaining' ? 'is-active' : ''} onClick={() => setMode('remaining')}>{t('partial.remaining')}</button>
          <button type="button" className={mode === 'used' ? 'is-active' : ''} onClick={() => setMode('used')}>{t('partial.used')}</button>
        </div>
        <Field
          autoFocus
          label={`${mode === 'remaining' ? t('partial.remainingQuantity') : t('partial.usedQuantity')} (${container.unit == null ? '' : unitLabel(container.unit, locale, numeric ?? 2)})`}
          inputMode="decimal"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          min="0"
          step="any"
          required
        />
        <div className="fraction-row" aria-label={t('partial.quickRemaining')}>
          {[0.25, 0.5, 0.75].map((ratio) => (
            <button key={ratio} type="button" onClick={() => {
              setMode('remaining');
              setValue(String(Math.round(current * ratio * 1_000) / 1_000));
            }}>
              {t('partial.remains', { fraction: ratio === 0.25 ? '¼' : ratio === 0.5 ? '½' : '¾' })}
            </button>
          ))}
        </div>
        <div className="before-after" aria-live="polite">
          <div><span>{t('common.before')}</span><strong>{formatNumber(current)} {container.unit == null ? '' : unitLabel(container.unit, locale, current)}</strong></div>
          <Icon name="arrow" />
          <div><span>{t('common.after')}</span><strong className={after != null && after < 0 ? 'text-danger' : ''}>{after == null ? '—' : `${formatNumber(after)} ${container.unit == null ? '' : unitLabel(container.unit, locale, after)}`}</strong></div>
        </div>
        <SelectField label={sourceAppliance != null && storagePolicyFor(sourceAppliance.equipmentKind).trackingMode === 'internal-only' ? t('partial.returnItem') : t('partial.returnContainer')} value={locationId} onChange={(event) => setLocationId(event.target.value)}>
          {snapshot.locations.filter((location) => compatibleApplianceIds.has(location.freezerId)).map((location) => (
            <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>
          ))}
        </SelectField>
        <Button type="submit" disabled={busyKeys.has(key) || after == null || after < 0 || after > current}>
          {t('partial.confirm')}
        </Button>
      </form>
    </Modal>
  );
};

export const MoveModal = ({
  container,
  snapshot,
  onClose,
}: {
  container: FoodContainer;
  snapshot: InventorySnapshot;
  onClose: () => void;
}) => {
  const { locale, t } = useI18n();
  const { service, performMutation, busyKeys } = useApp();
  const currentFreezerId =
    snapshot.locations.find((location) => location.id === container.locationId)?.freezerId ?? '';
  const currentAppliance = snapshot.freezers.find((appliance) => appliance.id === currentFreezerId);
  const compatibleFreezers = snapshot.freezers.filter(
    (freezer) =>
      currentAppliance != null &&
      freezer.archivedAt == null &&
      sharesStorageFamily(currentAppliance.equipmentKind, freezer.equipmentKind),
  );
  const [freezerId, setFreezerId] = useState(currentFreezerId);
  const [locationId, setLocationId] = useState(container.locationId);
  const destinationLocations = snapshot.locations.filter(
    (location) => location.freezerId === freezerId && location.archivedAt == null,
  );
  const destinationId =
    locationId && destinationLocations.some((location) => location.id === locationId)
      ? locationId
      : destinationLocations[0]?.id ?? '';
  const key = `move-modal:${container.id}`;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await performMutation(key, () => service.move(container, destinationId));
    if (result != null) onClose();
  };
  return (
    <Modal
      title={
        container.serialNumber == null
          ? t('move.titleItem', { reference: containerReference(container, locale) })
          : t('move.titleNumber', { number: container.serialNumber ?? '' })
      }
      description={containerDisplayName(snapshot, container, locale)}
      onClose={onClose}
    >
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <p className="muted">{t('move.current', { location: locationLabel(snapshot, container.locationId, locale) })}</p>
        <SelectField label={t('move.destinationAppliance')} value={freezerId} onChange={(event) => {
          const nextFreezer = event.target.value;
          setFreezerId(nextFreezer);
          setLocationId(snapshot.locations.find((location) => location.freezerId === nextFreezer)?.id ?? '');
        }}>
          {compatibleFreezers.map((freezer) => (
            <option key={freezer.id} value={freezer.id}>{snapshotDisplayLabel(snapshot, freezer.name, locale)}</option>
          ))}
        </SelectField>
        <SelectField label={t('move.newLocation')} value={locationId} onChange={(event) => setLocationId(event.target.value)}>
          <option value="">{t('location.none')}</option>
          {destinationLocations.map((location) => (
            <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>
          ))}
        </SelectField>
        <div className="before-after" aria-live="polite">
          <div><span>{t('common.before')}</span><strong>{locationLabel(snapshot, container.locationId, locale)}</strong></div>
          <Icon name="arrow" />
          <div><span>{t('common.after')}</span><strong>{destinationId ? locationLabel(snapshot, destinationId, locale) : '—'}</strong></div>
        </div>
        <Button type="submit" disabled={!destinationId || destinationId === container.locationId || busyKeys.has(key)} icon={<Icon name="move" />}>
          {t('move.confirm')}
        </Button>
      </form>
    </Modal>
  );
};

export const DiscardModal = ({
  container,
  snapshot,
  onClose,
}: {
  container: FoodContainer;
  snapshot: InventorySnapshot;
  onClose: () => void;
}) => {
  const { locale, t } = useI18n();
  const { service, performMutation, busyKeys } = useApp();
  const [reason, setReason] = useState('');
  const key = `discard-modal:${container.id}`;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await performMutation(key, () => service.discard(container, reason));
    if (result != null) onClose();
  };
  return (
    <Modal
      title={
        container.serialNumber == null
          ? t('discard.titleItem', { reference: containerReference(container, locale) })
          : t('discard.titleNumber', { number: container.serialNumber ?? '' })
      }
      description={`${containerDisplayName(snapshot, container, locale)} — ${
        container.trackingMode === 'visible-number'
          ? t('discard.freezerHelp')
          : t('discard.fridgeHelp')
      }`}
      onClose={onClose}
    >
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <SelectField label={t('discard.optionalReason')} value={reason} onChange={(event) => setReason(event.target.value)}>
          <option value="">{t('discard.noReason')}</option>
          {(['quality', 'packaging', 'forgotten', 'temperature', 'other'] as const).map((value) => {
            const label = t(`discard.reason.${value}` as MessageKey);
            return <option key={value} value={label}>{label}</option>;
          })}
        </SelectField>
        <p>{t('detail.quantity')}{t('common.separator')}{formatQuantity(container, locale)}</p>
        <Button type="submit" variant="danger" disabled={busyKeys.has(key)} icon={<Icon name="trash" />}>
          {t('discard.confirm')}
        </Button>
      </form>
    </Modal>
  );
};

export const ContainerDetailModal = ({
  container,
  snapshot,
  onClose,
  onStartExit,
}: {
  container: FoodContainer;
  snapshot: InventorySnapshot;
  onClose: () => void;
  onStartExit: () => void;
}) => {
  const i18n = useI18n();
  const { locale, t } = i18n;
  const { service, performMutation, undo, busyKeys } = useApp();
  const current = snapshot.containers.find((item) => item.id === container.id) ?? container;
  const currentLocation = snapshot.locations.find((location) => location.id === current.locationId);
  const currentAppliance = snapshot.freezers.find(
    (appliance) => currentLocation?.freezerId === appliance.id,
  );
  const compatibleLocationIds = new Set(
    snapshot.freezers
      .filter(
        (appliance) =>
          currentAppliance != null &&
          appliance.archivedAt == null &&
          sharesStorageFamily(currentAppliance.equipmentKind, appliance.equipmentKind),
      )
      .flatMap((appliance) =>
        snapshot.locations
          .filter((location) => location.freezerId === appliance.id && location.archivedAt == null)
          .map((location) => location.id),
      ),
  );
  const [subModal, setSubModal] = useState<'partial' | 'move' | 'discard' | null>(null);
  const [editing, setEditing] = useState(false);
  const [editBaseline, setEditBaseline] = useState(current);
  const [name, setName] = useState(current.name);
  const [barcode, setBarcode] = useState(current.barcode ?? '');
  const [locationId, setLocationId] = useState(current.locationId);
  const [remaining, setRemaining] = useState(current.remainingQuantity?.toString() ?? '');
  const [purchasedOn, setPurchasedOn] = useState(current.purchasedOn ?? '');
  const [purchaseDateCertainty, setPurchaseDateCertainty] = useState<FoodDateCertainty>(current.dateEvidence?.purchasedOn?.certainty ?? 'known');
  const [openedOn, setOpenedOn] = useState(current.openedOn ?? '');
  const [openDateCertainty, setOpenDateCertainty] = useState<FoodDateCertainty>(current.dateEvidence?.openedOn?.certainty ?? 'known');
  const [useFirstOn, setUseFirstOn] = useState(current.useFirstOn ?? '');
  const [useFirstDateCertainty, setUseFirstDateCertainty] = useState<FoodDateCertainty>(current.dateEvidence?.useFirstOn?.certainty ?? 'known');
  const [notes, setNotes] = useState(current.notes);
  const events = useMemo(
    () => snapshot.events
      .filter((event) => event.containerId === current.id)
      .sort((left, right) => right.occurredAt.localeCompare(left.occurredAt))
      .slice(0, 20),
    [snapshot.events, current.id],
  );
  const latestEvent = events[0] ?? null;

  const toggleEdit = () => {
    if (editing) { setEditing(false); return; }
    setEditBaseline(current);
    setName(current.name);
    setBarcode(current.barcode ?? '');
    setLocationId(current.locationId);
    setRemaining(current.remainingQuantity?.toString() ?? '');
    setPurchasedOn(current.purchasedOn ?? '');
    setPurchaseDateCertainty(current.dateEvidence?.purchasedOn?.certainty ?? 'known');
    setOpenedOn(current.openedOn ?? '');
    setOpenDateCertainty(current.dateEvidence?.openedOn?.certainty ?? 'known');
    setUseFirstOn(current.useFirstOn ?? '');
    setUseFirstDateCertainty(current.dateEvidence?.useFirstOn?.certainty ?? 'known');
    setNotes(current.notes);
    setEditing(true);
  };

  const saveEdit = async (event?: FormEvent, applyToCurrent = false) => {
    event?.preventDefault();
    const purchaseChanged = editBaseline.trackingMode === 'internal-only' &&
      (purchasedOn !== (editBaseline.purchasedOn ?? '') || purchaseDateCertainty !== (editBaseline.dateEvidence?.purchasedOn?.certainty ?? 'known'));
    const openChanged = editBaseline.trackingMode === 'internal-only' &&
      (openedOn !== (editBaseline.openedOn ?? '') || openDateCertainty !== (editBaseline.dateEvidence?.openedOn?.certainty ?? 'known'));
    const useFirstChanged = editBaseline.trackingMode === 'internal-only' &&
      (useFirstOn !== (editBaseline.useFirstOn ?? '') || useFirstDateCertainty !== (editBaseline.dateEvidence?.useFirstOn?.certainty ?? 'known'));
    const updates: Parameters<typeof service.correct>[1] = {
      ...(name !== editBaseline.name ? { name } : {}),
      ...(barcode !== (editBaseline.barcode ?? '') ? { barcode: barcode || null } : {}),
      ...(locationId !== editBaseline.locationId ? { locationId } : {}),
      ...(editBaseline.unit != null && numberOrNull(remaining) !== editBaseline.remainingQuantity ? { remainingQuantity: numberOrNull(remaining) } : {}),
      ...(purchaseChanged ? { purchasedOn: purchasedOn || null } : {}),
      ...(openChanged ? { openedOn: openedOn || null } : {}),
      ...(useFirstChanged ? { useFirstOn: useFirstOn || null } : {}),
      ...(purchaseChanged || openChanged || useFirstChanged ? { dateEvidence: {
        storageStartedOn: current.dateEvidence?.storageStartedOn ?? { source: 'household', certainty: 'known' },
        purchasedOn: purchaseChanged ? (purchasedOn ? { source: 'household', certainty: purchaseDateCertainty } : null) : current.dateEvidence?.purchasedOn ?? null,
        openedOn: openChanged ? (openedOn ? { source: 'household', certainty: openDateCertainty } : null) : current.dateEvidence?.openedOn ?? null,
        useFirstOn: useFirstChanged ? (useFirstOn ? { source: 'household', certainty: useFirstDateCertainty } : null) : current.dateEvidence?.useFirstOn ?? null,
        manufacturerDate: current.manufacturerDate == null ? null : current.dateEvidence?.manufacturerDate ?? { source: 'packaging', certainty: 'known' },
      } } : {}),
      ...(notes !== editBaseline.notes ? { notes } : {}),
    };
    if (Object.keys(updates).length === 0) { setEditing(false); return; }
    const result = await performMutation(`detail-edit:${current.id}`, () => service.correct(
      applyToCurrent ? current : editBaseline, updates,
    ));
    if (result != null) setEditing(false);
  };
  const restore = async () => {
    await performMutation(
      `detail-restore:${current.id}`,
      () => service.restore(current, current.locationId, current.initialQuantity),
    );
  };

  if (subModal === 'partial') {
    return <PartialModal container={current} snapshot={snapshot} onClose={() => setSubModal(null)} />;
  }
  if (subModal === 'move') {
    return <MoveModal container={current} snapshot={snapshot} onClose={() => setSubModal(null)} />;
  }
  if (subModal === 'discard') {
    return <DiscardModal container={current} snapshot={snapshot} onClose={() => setSubModal(null)} />;
  }

  return (
    <Modal title={`${containerReference(current, locale)} — ${containerDisplayName(snapshot, current, locale)}`} description={statusLabel(current.status, locale)} onClose={onClose} wide>
        <div className="detail-grid">
          <section className="detail-main">
            <dl className="facts">
              <div><dt>{t('detail.quantity')}</dt><dd>{formatQuantity(current, locale)}</dd></div>
              <div><dt>{t('detail.category')}</dt><dd>{categoryLabel(current.categoryId, locale)}</dd></div>
              {current.barcode != null ? <div><dt>{t('entry.barcode')}</dt><dd>{current.barcode}</dd></div> : null}
              <div><dt>{t('detail.location')}</dt><dd>{locationLabel(snapshot, current.locationId, locale)}</dd></div>
              <div><dt>{currentAppliance?.equipmentKind === 'pantry' ? t('entry.storagePantryDate') : current.trackingMode === 'internal-only' ? t('entry.storageFridgeDate') : t('entry.storageFreezerDate')}</dt><dd>{formatDate(current.storageStartedOn, locale)}<small>{t('dates.sourceHousehold')} · {t(current.dateEvidence?.storageStartedOn.certainty === 'estimated' ? 'dates.estimated' : 'dates.known')}</small></dd></div>
              {current.trackingMode === 'internal-only' ? (
                <>
                  <div><dt>{t('detail.purchaseDate')}</dt><dd>{formatDate(current.purchasedOn ?? null, locale)}{current.purchasedOn != null ? <small>{t('dates.sourceHousehold')} · {t(current.dateEvidence?.purchasedOn?.certainty === 'estimated' ? 'dates.estimated' : 'dates.known')}</small> : null}</dd></div>
                  <div><dt>{t('detail.openDate')}</dt><dd>{formatDate(current.openedOn ?? null, locale)}{current.openedOn != null ? <small>{t('dates.sourceHousehold')} · {t(current.dateEvidence?.openedOn?.certainty === 'estimated' ? 'dates.estimated' : 'dates.known')}</small> : null}</dd></div>
                  <div><dt>{t('detail.referenceDate')}</dt><dd>{formatDate(current.useFirstOn ?? null, locale)}{current.useFirstOn != null ? <small>{t('dates.sourceHousehold')} · {t(current.dateEvidence?.useFirstOn?.certainty === 'estimated' ? 'dates.estimated' : 'dates.known')}</small> : null}</dd></div>
                </>
              ) : null}
              <div><dt>{current.manufacturerDateType === 'none' ? t('detail.manufacturerDate') : current.manufacturerDateType}</dt><dd>{formatDate(current.manufacturerDate, locale)}{current.manufacturerDate != null ? <small>{t('dates.sourcePackaging')} · {t(current.dateEvidence?.manufacturerDate?.certainty === 'estimated' ? 'dates.estimated' : 'dates.known')}</small> : null}</dd></div>
              <div><dt>{t('detail.bestUseDate')}</dt><dd>{formatDate(current.qualityHorizonOn, locale)}</dd></div>
              {current.numberAssignedAt != null ? (
                <div><dt>{t('detail.numberAssigned')}</dt><dd>{i18n.formatDate(new Date(current.numberAssignedAt), { dateStyle: 'short', timeStyle: 'short' })}</dd></div>
              ) : null}
              {current.numberReleasedAt != null ? (
                <div>
                  <dt>{current.numberReleasedAt > new Date().toISOString() ? t('detail.numberReservedUntil') : t('detail.numberReleased')}</dt>
                  <dd>{i18n.formatDate(new Date(current.numberReleasedAt), { dateStyle: 'short', timeStyle: 'short' })}</dd>
                </div>
              ) : null}
            </dl>

            {current.horizonRule != null ? (
              <div className="notice notice--neutral">
                <Icon name="info" />
                <div>
                  <strong>{t(`rotation.${current.horizonRule.id}` as MessageKey)}</strong>
                  <p>{t('rotation.source')}{t('common.separator')}{t('entry.version', { version: current.horizonRule.version })}</p>
                  {current.horizonRule.sourceUrl != null ? (
                    <p><a href={current.horizonRule.sourceUrl} target="_blank" rel="noreferrer">{t('detail.viewSource')}</a></p>
                  ) : null}
                  <p>{t('rotation.disclaimer')}</p>
                </div>
              </div>
            ) : null}

            {current.incidentStatus === 'unresolved' ? (
              <div className="notice notice--danger">
                <Icon name="warning" />
                <div>
                  <strong>{t('detail.incident')}</strong>
                  <p>{t('detail.incidentHelp')}</p>
                  {current.incidentNote ? <p>{current.incidentNote}</p> : null}
                </div>
              </div>
            ) : null}
            {current.auditFlag !== 'ok' ? (
              <div className="notice notice--warning">
                <Icon name="audit" />
                <p>{t('detail.auditHelp')}</p>
              </div>
            ) : null}

            {['present', 'partial'].includes(current.status) ? (
              <div className="action-grid">
                <Button onClick={onStartExit} icon={<Icon name="check" />}>
                  {current.trackingMode === 'internal-only' ? t('detail.consumeItem') : t('detail.exitContainer')}
                </Button>
                <Button variant="secondary" onClick={() => setSubModal('move')} icon={<Icon name="move" />}>{t('detail.move')}</Button>
                <Button variant="ghost" onClick={() => setSubModal('discard')} icon={<Icon name="trash" />}>{t('detail.discard')}</Button>
              </div>
            ) : null}
            {current.status === 'draft' ? (
              <Button onClick={() => void performMutation(`detail-confirm:${current.id}`, () => service.confirm(current))}>
                {t('detail.numberWritten')}
              </Button>
            ) : null}
            {['out', 'discarded'].includes(current.status) ? (
              <Button onClick={() => void restore()} icon={<Icon name="undo" />}>{t('detail.restore')}</Button>
            ) : null}
            {current.auditFlag !== 'ok' && ['present', 'partial'].includes(current.status) ? (
              <Button variant="secondary" onClick={() => void performMutation(`detail-present:${current.id}`, () => service.confirmPresent(current))}>
                {t('detail.confirmPresence')}
              </Button>
            ) : null}

            <button className="disclosure" type="button" onClick={toggleEdit} aria-expanded={editing}>
              <span>{t('detail.edit')}</span><span aria-hidden="true">{editing ? '−' : '+'}</span>
            </button>
            {editing ? (
              <form className="form-stack advanced-fields" onSubmit={(event) => void saveEdit(event)}>
                <Field label={t('detail.name')} value={name} onChange={(event) => setName(event.target.value)} required />
                <Field label={t('entry.barcode')} value={barcode} inputMode="numeric" pattern="[0-9]{8,14}" maxLength={14} onChange={(event) => setBarcode(event.target.value.replace(/\D/gu, '').slice(0, 14))} hint={t('entry.barcodeHelp')} />
                <SelectField label={t('detail.location')} value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                  {snapshot.locations
                    .filter((location) => compatibleLocationIds.has(location.id))
                    .map((location) => <option key={location.id} value={location.id}>{locationLabel(snapshot, location.id, locale)}</option>)}
                </SelectField>
                {current.unit != null ? (
                  <Field label={t('detail.remainingQuantity', { unit: unitLabel(current.unit, locale, Number(remaining) || 2) })} inputMode="decimal" value={remaining} onChange={(event) => setRemaining(event.target.value)} />
                ) : null}
                {current.trackingMode === 'internal-only' ? (
                  <div className="fridge-dates-panel">
                    <div className="form-row">
                      <Field label={t('detail.purchaseDate')} type="date" value={purchasedOn} onChange={(event) => setPurchasedOn(event.target.value)} />
                      <Field label={t('detail.openDate')} type="date" value={openedOn} min={purchasedOn || undefined} onChange={(event) => setOpenedOn(event.target.value)} />
                    </div>
                    <div className="form-row">
                      <SelectField label={t('dates.purchaseCertainty')} value={purchaseDateCertainty} onChange={(event) => setPurchaseDateCertainty(event.target.value as FoodDateCertainty)} disabled={!purchasedOn}><option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option></SelectField>
                      <SelectField label={t('dates.openCertainty')} value={openDateCertainty} onChange={(event) => setOpenDateCertainty(event.target.value as FoodDateCertainty)} disabled={!openedOn}><option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option></SelectField>
                    </div>
                    <Field label={t('detail.useFirst')} type="date" value={useFirstOn} onChange={(event) => setUseFirstOn(event.target.value)} hint={t('detail.useFirstHint')} />
                    <SelectField label={t('dates.referenceCertainty')} value={useFirstDateCertainty} onChange={(event) => setUseFirstDateCertainty(event.target.value as FoodDateCertainty)} disabled={!useFirstOn}><option value="known">{t('dates.known')}</option><option value="estimated">{t('dates.estimated')}</option></SelectField>
                  </div>
                ) : null}
                <label className="field">
                  <span className="field__label">{t('detail.notes')}</span>
                  <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} maxLength={2_000} />
                </label>
                {current.version !== editBaseline.version ? (
                  <div className="notice notice--neutral">
                    <p>{t('detail.editConflict')}</p>
                    <Button type="button" variant="secondary" onClick={() => void saveEdit(undefined, true)} disabled={busyKeys.has(`detail-edit:${current.id}`)}>{t('detail.applyDraft')}</Button>
                  </div>
                ) : null}
                <Button type="submit" disabled={busyKeys.has(`detail-edit:${current.id}`)}>{t('detail.save')}</Button>
              </form>
            ) : null}

          </section>

          <aside className="history-panel" aria-label={t('detail.historyAria')}>
            <h3>{t('detail.history')}</h3>
            {events.length === 0 ? <p className="muted">{t('detail.noEvent')}</p> : (
              <ol className="timeline">
                {events.map((event) => (
                  <li key={event.id}>
                    <span>{i18n.formatDate(new Date(event.occurredAt), { dateStyle: 'short', timeStyle: 'short' })}</span>
                    <strong>{t(`event.${event.eventType}` as MessageKey)}</strong>
                    {event.payload.reason ? <small>{event.payload.reason}</small> : null}
                  </li>
                ))}
              </ol>
            )}
            {latestEvent?.payload.before != null && latestEvent.payload.after?.version === current.version && latestEvent.eventType !== 'undo' ? (
              <Button variant="ghost" onClick={() => void undo(latestEvent)} icon={<Icon name="undo" />}>
                {t('detail.undoLast')}
              </Button>
            ) : null}
          </aside>
        </div>
      </Modal>
  );
};
